/* Native cart enhancement. Shopify renders prices, discounts and the resulting quantities. */
if (!customElements.get('throne-cart')) {
  customElements.define('throne-cart', class extends HTMLElement {
    pendingQuantities = new Map();

    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      if (!this.dialog?.showModal || !this.form) return;
      this.setAttribute('data-cart-ready', '');
      document.addEventListener('click', event => this.onClick(event), options);
      document.addEventListener('submit', event => this.onSubmit(event), options);
      this.addEventListener('change', event => {
        if (event.target.matches('[data-cart-quantity]')) this.change(event.target);
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right) this.close();
      }, options);
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const controls = [...this.dialog.querySelectorAll('a[href], button, input, select, textarea, summary, [tabindex]')]
          .filter(control => !control.disabled && control.tabIndex >= 0 && control.getClientRects().length);
        const first = controls[0];
        const last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first || !event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          (event.shiftKey ? last : first)?.focus();
        }
      }, options);
      document.addEventListener('shopify:section:select', event => {
        if (event.target.contains(this)) this.open();
      }, options);
      document.addEventListener('shopify:section:deselect', event => {
        if (event.target.contains(this)) this.close();
      }, options);
      this.syncCount();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.pendingQuantities.clear();
      this.closeRequest = null;
      this.dialog?.close();
      this.dialog?.removeAttribute('data-visible');
      this.setBusy(false);
    }

    onClick(event) {
      const trigger = event.target.closest('[data-cart-open]');
      if (trigger && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
        event.preventDefault();
        this.open(trigger);
        if (!this.busy) this.load();
        return;
      }
      if (!this.contains(event.target)) return;
      if (event.target.closest('[data-cart-close]')) this.close();
      const remove = event.target.closest('[data-cart-remove]');
      const step = event.target.closest('[data-cart-step]');
      if (!remove && !step) return;
      event.preventDefault();
      if (this.unavailable || this.busy && (!this.optimistic || remove)) return;
      const input = event.target.closest('[data-cart-line]').querySelector('[data-cart-quantity]');
      if (remove) this.change(input, 0);
      else {
        if (step.dataset.cartStep === '1') input.stepUp();
        else input.stepDown();
        this.change(input);
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (form === this.form) {
        // A blur/change can start a quantity update immediately before checkout.
        if (this.busy || this.pendingQuantities.size || this.unavailable) event.preventDefault();
        return;
      }
      // Only enhance the theme's product form; app-owned and accelerated checkout stay native.
      if (!form.matches('form.pdp-form') || event.defaultPrevented) return;
      if (new URL(form.action).pathname !== this.dataset.addUrl) return;
      event.preventDefault();
      if (this.busy) return;
      const data = new FormData(form);
      data.delete('return_to');
      data.set('sections', this.sectionIds.join(','));
      data.set('sections_url', this.dataset.cartUrl);
      this.open(event.submitter);
      this.mutate(this.dataset.addUrl + '.js', data, form);
    }

    open(trigger) {
      this.closeRequest = null;
      if (trigger) this.returnFocus = trigger;
      const menu = document.querySelector('[data-nav-toggle][aria-expanded="true"]');
      if (menu) menu.click();
      if (!this.dialog.open) {
        this.dialog.showModal();
        // Establish both starting styles before transitioning the new top-layer elements.
        getComputedStyle(this.dialog).transform;
        getComputedStyle(this.dialog, '::backdrop').opacity;
      }
      this.dialog.setAttribute('data-visible', '');
    }

    async close() {
      if (!this.dialog.open || this.closeRequest) return;
      const request = this.closeRequest = {};
      this.dialog.removeAttribute('data-visible');
      // Keep the backdrop, scroll lock and focus containment until both transitions finish.
      await Promise.allSettled(this.dialog.getAnimations({ subtree: true }).map(animation => animation.finished));
      if (this.closeRequest !== request) return;
      this.closeRequest = null;
      this.dialog.close();
    }

    get sectionIds() {
      return [this.dataset.sectionId, document.querySelector('[data-cart-page]')?.dataset.sectionId].filter(Boolean);
    }

    async load() {
      this.clearError();
      this.setBusy(true);
      try {
        await this.refresh();
      } catch {
        this.unavailable = true;
        this.showError(this.dataset.errorMessage);
      } finally {
        this.setBusy(false);
      }
      if (this.isConnected && this.dialog.open && !this.closeRequest) {
        this.querySelector('[data-cart-close]').focus({ preventScroll: true });
        this.querySelector('[data-cart-status]').textContent = this.querySelector('[data-cart-announcement]')?.textContent || '';
      }
    }

    async change(input, quantity = Number(input.value)) {
      if (this.unavailable || this.busy && !this.optimistic) return;
      if (quantity !== 0 && (!Number.isInteger(quantity) || !input.reportValidity())) return;
      const line = input.closest('[data-cart-line]');
      if (quantity === 0) {
        if (this.busy) return;
        const body = JSON.stringify({ id: line.dataset.key, quantity, sections: this.sectionIds, sections_url: this.dataset.cartUrl });
        return this.mutate(this.dataset.changeUrl + '.js', body, null, { key: line.dataset.key, quantity });
      }
      this.pendingQuantities.set(line.dataset.identity, quantity);
      this.applyPendingQuantities();
      if (this.busy) return;
      // Serialize writes and coalesce repeated taps to the latest requested quantity.
      // Resolve the current Shopify key again after every response: discounts can change it.
      while (this.pendingQuantities.size && this.isConnected && !this.unavailable) {
        const [identity, desired] = this.pendingQuantities.entries().next().value;
        const current = [...this.querySelectorAll('[data-cart-line]')].find(item => item.dataset.identity === identity);
        if (!current) { this.pendingQuantities.delete(identity); continue; }
        const body = JSON.stringify({ id: current.dataset.key, quantity: desired, sections: this.sectionIds, sections_url: this.dataset.cartUrl });
        await this.mutate(this.dataset.changeUrl + '.js', body, null, { identity, quantity: desired, optimistic: true });
      }
    }

    applyPendingQuantities(reset = false) {
      const lines = [...this.querySelectorAll('[data-cart-line]')];
      for (const identity of this.pendingQuantities.keys()) {
        if (!lines.some(line => line.dataset.identity === identity)) this.pendingQuantities.delete(identity);
      }
      for (const line of lines) {
        const quantity = reset ? Number(line.dataset.quantity) : this.pendingQuantities.get(line.dataset.identity);
        if (quantity === undefined) continue;
        const input = line.querySelector('[data-cart-quantity]');
        const active = document.activeElement;
        input.value = quantity;
        line.querySelector('[data-cart-step="-1"]').disabled = quantity <= Number(input.min || 1);
        line.querySelector('[data-cart-step="1"]').disabled = input.max !== '' && quantity >= Number(input.max);
        if (active?.disabled && line.contains(active)) line.querySelector('[data-cart-step]:not(:disabled)')?.focus({ preventScroll: true });
      }
      this.syncCount(true);
    }

    async mutate(url, body, productForm, focus) {
      this.clearError();
      this.setBusy(true, productForm, focus?.optimistic);
      let errorMessage;
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: typeof body === 'string' ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
          body
        });
        const result = await response.json();
        if (!response.ok) {
          errorMessage = typeof result.description === 'string' ? result.description : this.dataset.errorMessage;
          throw new Error('Cart request rejected');
        }
        if (!this.isConnected) return;
        if (focus?.optimistic && this.pendingQuantities.get(focus.identity) === focus.quantity) {
          this.pendingQuantities.delete(focus.identity);
        }
        // A mutation can succeed even if Shopify fails to render one of the sections.
        try { this.render(result.sections); } catch { await this.refresh(); }
      } catch {
        if (!this.isConnected) return;
        this.pendingQuantities.clear();
        if (focus?.optimistic) this.applyPendingQuantities(true);
        // Never retry a POST: an uncertain add may already have succeeded.
        // Refresh also handles Shopify's partial-add / inventory-limit responses.
        try { await this.refresh(); } catch { this.unavailable = true; }
        this.showError(errorMessage || this.dataset.errorMessage);
      } finally {
        this.setBusy(false);
      }
      if (!this.isConnected || !this.dialog.open || this.closeRequest) return;
      if (focus?.optimistic) {
        if (!this.pendingQuantities.size) this.querySelector('[data-cart-status]').textContent = this.querySelector('[data-cart-announcement]')?.textContent || '';
        return;
      }
      const line = focus?.quantity !== 0 && [...this.querySelectorAll('[data-cart-line]')].find(item => item.dataset.key === focus?.key);
      const target = line?.querySelector('[data-cart-quantity]') || this.querySelector('[data-cart-close]');
      target.focus({ preventScroll: true });
      this.querySelector('[data-cart-status]').textContent = this.querySelector('[data-cart-announcement]')?.textContent || '';
    }

    async refresh() {
      const url = new URL(this.dataset.cartUrl, location.origin);
      url.searchParams.set('sections', this.sectionIds.join(','));
      // Accept: application/json makes /cart return cart data instead of section HTML.
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('Cart refresh failed');
      const sections = await response.json();
      if (this.isConnected) this.render(sections);
    }

    render(sections) {
      const html = sections?.[this.dataset.sectionId];
      if (!html) throw new Error('Cart section missing');
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-render]');
      if (!next) throw new Error('Cart markup missing');
      const current = this.querySelector('[data-cart-render]');
      const scrollTop = current.querySelector('.cart-drawer__content').scrollTop;
      const formScrollTop = this.form.scrollTop;
      const active = document.activeElement;
      const activeLine = current.contains(active) ? active.closest('[data-cart-line]') : null;
      if (this.busy && !this.optimistic) next.classList.add('cart-drawer__render--revealed');
      current.replaceWith(next);
      next.querySelector('.cart-drawer__content').scrollTop = scrollTop;
      this.form.scrollTop = formScrollTop;
      const page = document.querySelector('[data-cart-page]');
      if (page && sections[page.dataset.sectionId]) {
        const nextPage = new DOMParser().parseFromString(sections[page.dataset.sectionId], 'text/html').querySelector('[data-cart-page]');
        if (nextPage) page.replaceWith(nextPage);
      }
      this.unavailable = false;
      if (this.optimistic) {
        this.applyPendingQuantities();
        // Re-rendering must not enable checkout while another quantity write is queued.
        const checkout = this.form.querySelector('[name="checkout"]');
        if (checkout) checkout.disabled = true;
        if (activeLine && this.dialog.open && !this.closeRequest) {
          const line = [...next.querySelectorAll('[data-cart-line]')].find(item => item.dataset.identity === activeLine.dataset.identity);
          const selector = active.matches('[data-cart-quantity]') ? '[data-cart-quantity]'
            : active.dataset.cartStep ? `[data-cart-step="${active.dataset.cartStep}"]` : null;
          if (selector) {
            const target = line?.querySelector(selector);
            (target?.disabled ? line.querySelector('[data-cart-step]:not(:disabled)') : target)?.focus({ preventScroll: true });
          }
        }
      }
      this.syncCount();
    }

    syncCount(optimistic = false) {
      let count = Number(this.querySelector('[data-cart-render]').dataset.cartCount);
      for (const line of this.querySelectorAll('[data-cart-line]')) {
        count += Number(line.querySelector('[data-cart-quantity]').value) - Number(line.dataset.quantity);
      }
      this.querySelector('.cart-drawer__count').textContent = count;
      document.querySelectorAll('[data-cart-badge]').forEach(badge => { badge.textContent = count; });
      if (!optimistic && !this.pendingQuantities.size) document.dispatchEvent(new CustomEvent('throne:cart-updated'));
    }

    setBusy(busy, productForm, optimistic = false) {
      this.busy = busy;
      this.optimistic = busy && optimistic;
      this.dialog?.setAttribute('aria-busy', String(busy && !optimistic));
      if (busy) {
        document.dispatchEvent(new CustomEvent('throne:cart-updating'));
        this.disabledControls = new Map();
        const controls = [...this.form.elements, ...(productForm?.elements || [])];
        controls.forEach(control => {
          if (control.matches('[data-cart-close]') || optimistic && !control.matches('[name="checkout"], [name="update"]')) return;
          this.disabledControls.set(control, control.disabled);
          control.disabled = true;

        });
        const message = productForm ? this.dataset.addingMessage : this.dataset.updatingMessage;
        this.querySelector('[data-cart-status]').textContent = message;
      } else {
        this.disabledControls?.forEach((disabled, control) => { control.disabled = disabled; });
        this.disabledControls = null;
        // Section rendering replaces the original disabled checkout element.
        this.form.querySelector?.('[name="checkout"]')?.toggleAttribute('disabled', Boolean(this.unavailable || this.pendingQuantities.size));
        if (this.unavailable) [...this.form.elements].forEach(control => {
          if (!control.matches('[data-cart-close]')) control.disabled = true;
        });
      }
    }

    clearError() {
      this.querySelector('[data-cart-error]').hidden = true;
    }

    showError(message) {
      this.querySelector('[data-cart-error-text]').textContent = message;
      this.querySelector('[data-cart-error]').hidden = false;
    }
  });
}
